import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Users, Download, Sparkles, Coins, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const PROGRAMS = [
  { key: "confidence-arise", label: "Confidence Arise", icon: Sparkles, accent: "gold" },
  { key: "million-dollar-mindset", label: "Million Dollar Mindset", icon: Coins, accent: "burgundy" },
];

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  return `"${s.replace(/"/g, '""').replace(/\r?\n/g, " ")}"`;
}

function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const headers = ["created_at", "program", "name", "email", "phone", "message"];
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push(headers.map((h) => csvEscape(r[h])).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Admin-only waitlist widget for native programs. Mounted on /profile.
 * Shows total + this-week counts per program and lets the admin CSV-export.
 * Hooked to GET /api/admin/program-interest.
 */
export default function AdminWaitlistPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/program-interest", { params: { limit: 500 } });
        if (alive) setRows(data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load waitlist");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const stats = useMemo(() => {
    const cutoff = Date.now() - WEEK_MS;
    const out = {};
    for (const p of PROGRAMS) {
      const all = rows.filter((r) => r.program === p.key);
      const recent = all.filter((r) => new Date(r.created_at).getTime() >= cutoff);
      out[p.key] = {
        total: all.length,
        weekly: recent.length,
        latest: all.slice(0, 3),
      };
    }
    return out;
  }, [rows]);

  const exportProgram = (programKey, label) => {
    const subset = rows.filter((r) => r.program === programKey);
    if (subset.length === 0) {
      toast.error("Nothing to export yet for this program.");
      return;
    }
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`jju-waitlist-${programKey}-${stamp}.csv`, subset);
    toast.success(`Exported ${subset.length} ${label} sign-ups`);
  };

  return (
    <div className="jj-card mt-6 p-6 sm:p-7 border-burgundy/30 bg-gradient-to-br from-cream to-cream-surface" data-testid="admin-waitlist-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="max-w-xl">
          <div className="overline text-burgundy flex items-center gap-2">
            <Users className="h-3.5 w-3.5" /> Admin · Program waitlists
          </div>
          <h2 className="font-serif text-2xl text-jjblue mt-1" data-testid="admin-waitlist-heading">
            {loading ? "Loading…" : `${rows.length} total sign-up${rows.length === 1 ? "" : "s"}.`}
          </h2>
          <p className="mt-1.5 text-sm text-[var(--jj-text-secondary)]">
            Track interest across the native programs. Export to CSV when you're ready to open the next cohort.
          </p>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        {PROGRAMS.map((p) => {
          const Icon = p.icon;
          const s = stats[p.key] || { total: 0, weekly: 0, latest: [] };
          const accentRing = p.accent === "gold" ? "ring-[var(--jj-gold)]/50" : "ring-[var(--jj-burgundy)]/40";
          const accentBg = p.accent === "gold" ? "bg-gold/15 text-burgundy" : "bg-burgundy/10 text-burgundy";
          return (
            <div key={p.key} className={`jj-card bg-white p-5 ring-1 ${accentRing}`} data-testid={`waitlist-${p.key}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`h-9 w-9 rounded-xl ${accentBg} flex items-center justify-center shrink-0`}>
                    <Icon className="h-4 w-4" strokeWidth={1.6} />
                  </span>
                  <h3 className="font-serif text-lg text-jjblue truncate">{p.label}</h3>
                </div>
                <button
                  onClick={() => exportProgram(p.key, p.label)}
                  className="text-xs text-burgundy inline-flex items-center gap-1 hover:underline"
                  data-testid={`waitlist-export-${p.key}`}
                  title="Download CSV"
                >
                  <Download className="h-3.5 w-3.5" /> CSV
                </button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-cream-surface border border-jj p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--jj-muted)]">Total</div>
                  <div className="font-serif text-3xl text-jjblue mt-0.5 leading-none" data-testid={`waitlist-${p.key}-total`}>{s.total}</div>
                </div>
                <div className="rounded-xl bg-cream-surface border border-jj p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--jj-muted)] flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> This week
                  </div>
                  <div className={`font-serif text-3xl mt-0.5 leading-none ${s.weekly > 0 ? "text-burgundy" : "text-jjblue/60"}`} data-testid={`waitlist-${p.key}-weekly`}>{s.weekly}</div>
                </div>
              </div>

              {s.latest.length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--jj-muted)] mb-2">Most recent</div>
                  <ul className="space-y-1.5">
                    {s.latest.map((r) => (
                      <li key={r.id} className="text-xs text-jjblue/85 flex items-center justify-between gap-2 min-w-0">
                        <span className="truncate">{r.name || "(no name)"}{" · "}<a href={`mailto:${r.email}`} className="text-burgundy hover:underline">{r.email}</a></span>
                        <span className="text-[10px] text-[var(--jj-muted)] shrink-0">{new Date(r.created_at).toLocaleDateString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {s.total === 0 && (
                <div className="mt-4 text-xs text-[var(--jj-muted)]">No sign-ups yet — share the link to start filling the seats.</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
